import React, { useEffect, useState } from 'react';
import { Alert, Select } from 'antd';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { State } from '../interfaces';
import { getStatesByCountryName } from '../../services';
import { setTaxEstimateAddress } from '../../redux/slices/shoppingCartSlice';
import './salesTaxEstimator.scss';

const SalesTaxEstimator: React.FC = () => {
  const dispatch = useAppDispatch();
  const shipping = useAppSelector((state) => state.user.shipping);
  const taxEstimateAddress = useAppSelector((state) => state.shoppingCart.taxEstimateAddress);
  const [states, setStates] = useState<State[]>([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [statesError, setStatesError] = useState<string | null>(null);

  const normalizedShippingCountry = String(shipping?.country || '').trim().toLowerCase();
  const shouldShowEstimator =
    !normalizedShippingCountry ||
    normalizedShippingCountry === 'us' ||
    normalizedShippingCountry === 'usa' ||
    normalizedShippingCountry === 'united states' ||
    normalizedShippingCountry === 'united states of america';

  useEffect(() => {
    if (!shouldShowEstimator) {
      return;
    }

    let active = true;

    const loadStates = async () => {
      setLoadingStates(true);
      setStatesError(null);

      try {
        const response = await getStatesByCountryName('USA');
        if (active) {
          setStates(response);
        }
      } catch (error) {
        if (active) {
          setStatesError(
            error instanceof Error ? error.message : 'Failed to load states for tax estimate.'
          );
        }
      } finally {
        if (active) {
          setLoadingStates(false);
        }
      }
    };

    loadStates();

    return () => {
      active = false;
    };
  }, [shouldShowEstimator]);

  if (!shouldShowEstimator) {
    return null;
  }

  const handleProvinceChange = (value: string) => {
    dispatch(
      setTaxEstimateAddress({
        province: value,
        country: 'USA',
      })
    );
  };

  return (
    <div className="sales-tax-estimator">
      <p className="sales-tax-estimator__title">Estimate US sales tax by state</p>
      <div className="sales-tax-estimator__controls">
        <Select
          id="sales-tax-state"
          aria-label="State for sales tax estimate"
          showSearch
          placeholder="Select state"
          value={taxEstimateAddress.province || undefined}
          onChange={handleProvinceChange}
          loading={loadingStates}
          disabled={loadingStates || Boolean(statesError)}
          optionFilterProp="label"
          options={states.map((state) => ({
            value: state.name,
            label: state.name,
          }))}
        />
      </div>
      {statesError && <Alert type="error" showIcon message={statesError} />}
    </div>
  );
};

export default SalesTaxEstimator;
